import { FC, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@ibsys/kit';
import type { CriterionAverageItem, IndicatorSelectHandler } from '@/entities/dashboards';
import { DASHBOARD_BLOCK_SCROLL_CLASS, qualityZoneByRatio, type QualityZoneRanges } from '@/shared/lib';
import { EmptyState } from '@/shared/ui';
import { BarChart, type BarChartItem } from '@/widgets/BarChart';
import { useCriteriaOrder } from '../model/useCriteriaCatalog';

type Props = {
  title: string;
  /** Средние по видимым (не скрытым) критериям; в графике переупорядочиваются по порядку отчёта. */
  criteria: CriterionAverageItem[];
  /** Пользовательские пороги зон качества (окраска столбца по доле от максимума). */
  zoneRanges: QualityZoneRanges;
  /** Переход к диалогам критерия; не задан — строки некликабельны. */
  onSelect?: IndicatorSelectHandler;
};

/** Группа критериев одного экспертного заключения. */
type CriteriaGroup = {
  reportId: number;
  reportName: string;
  items: BarChartItem[];
};

/**
 * Карточка «Средние баллы по критериям»: критерии сгруппированы по отчётам-владельцам,
 * рядом со средним баллом показан максимум шкалы, а столбец окрашен по доле среднего от
 * максимума (`value/maxScore`) по единой зелёно-жёлто-красной шкале зон — критерий с коротким
 * потолком, где консультанты близки к максимуму, не уходит в красный лишь из-за короткой шкалы.
 * Критерий без максимума окраску по доле не получает. Критерии с нулевым потолком шкалы
 * (`maxScore === 0`) сюда уже не доходят — их отсеивает единый видимый список критериев
 * (`useDashboardData`), чтобы нулевой критерий не всплывал лишним столбцом в графике и
 * слабейшим/проблемным в KPI и сводке.
 *
 * Порядок отчётов и блоков внутри них — общий по каталогу критериев (`useCriteriaOrder`), тот же,
 * что в сводке и в «Настройках отображения». Отчёты и критерии, которых нет в каталоге, уходят
 * в конец, сохраняя исходный порядок.
 */
export const CriteriaAveragesCard: FC<Props> = ({ title, criteria, zoneRanges, onSelect }) => {
  const { reportRank, criterionRank } = useCriteriaOrder();

  const groups = useMemo((): CriteriaGroup[] => {
    const byReport = new Map<number, CriteriaGroup>();
    for (const c of criteria) {
      let group = byReport.get(c.expertReportId);
      if (!group) {
        group = { reportId: c.expertReportId, reportName: c.expertReportName, items: [] };
        byReport.set(c.expertReportId, group);
      }
      const hasMax = c.maxScore !== null && c.maxScore > 0;
      group.items.push({
        label: c.name,
        value: c.value,
        maxScore: c.maxScore,
        zone: hasMax ? qualityZoneByRatio(c.value / (c.maxScore as number), zoneRanges) : undefined,
        selection: c.selection,
      });
    }

    // Блоки внутри отчёта — в порядке заключения; сортировка стабильна, поэтому блоки вне каталога
    // остаются в конце в исходном порядке бэка.
    for (const group of byReport.values()) {
      group.items.sort(
        (a, b) => criterionRank(group.reportId, a.label) - criterionRank(group.reportId, b.label),
      );
    }
    // Отчёты — в порядке каталога; отсутствующие в каталоге — в конце, в порядке первого появления.
    return [...byReport.values()].sort((a, b) => reportRank(a.reportId) - reportRank(b.reportId));
  }, [criteria, zoneRanges, reportRank, criterionRank]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent>
        {groups.length === 0 ? (
          <EmptyState />
        ) : (
          <div className={`${DASHBOARD_BLOCK_SCROLL_CLASS} flex flex-col gap-4`}>
            {groups.map((group) => (
              <div key={group.reportId} className="flex flex-col gap-2">
                <h4 className="text-sm font-medium text-muted-foreground">{group.reportName}</h4>
                {/* Ширина бара нормируется долей value/maxScore самого критерия, поэтому общий maxValue не нужен. */}
                <BarChart items={group.items} onSelect={onSelect} />
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
